// blueprint-gate-hash.ts
//
// B3 (plan 20260915-1650, phase-00). Two pure primitives behind "G0 is bound
// to the bytes of BLUEPRINT.md":
//
//  - `computeBlueprintHash` — sha256 of the file's RAW bytes, lowercase hex.
//    This must equal what the init caller declared as
//    `blueprint.bytes_sha256` (init-schema.ts). Measured on the only real
//    caller, `notes/autopilot-audit-2026-09-14/run-real-claude-dogfood.mjs`:
//    it hashes the utf8 bytes it wrote to disk — so no whitespace or
//    line-ending normalisation here, or the two sides drift apart. The phase's
//    risk table is explicit: normalise only after real noise is MEASURED.
//
//  - `assertNonGoalsPresent` — spec §13: "không có non-goal thì không được
//    duyệt". Matches the template's heading `## Ngoài phạm vi` EXACTLY
//    (BLUEPRINT.tpl.md:35) and fails closed: a missing heading, an empty
//    section, or the untouched `<mục 1>` placeholder all read as "no
//    non-goals declared". A near-miss heading (`## Ngoài phạm vi kỹ thuật`)
//    does not count — guessing which heading the author meant is how a gate
//    condition quietly stops applying.
//
// Nothing here reads gates.json or git; the callers do (mission-blueprint-
// binding.ts at init, g0-blueprint-recheck-before-dispatch.ts at dispatch).

import { createHash } from "node:crypto";
import { computeFileSha256 } from "../filesystem-helpers.js";

/** The template heading, verbatim. Exported so tests and docs pin the same string. */
export const NON_GOALS_HEADING = "## Ngoài phạm vi";

/** A template placeholder bullet such as `<mục 1>` — present, but not a declaration. */
const PLACEHOLDER_BULLET = /^<[^>]*>$/;

/** Any markdown heading, or a horizontal rule — ends the non-goals section. */
const SECTION_END = /^(#{1,6}\s|---\s*$)/;

/** A list item: `- x`, `* x`, `+ x`. Group 1 is the item text. */
const BULLET = /^[-*+]\s+(.*)$/;

/** sha256 of a byte buffer, lowercase hex — the hash the init side declares. */
export function sha256HexOfBytes(bytes: Uint8Array): string {
  return createHash("sha256").update(bytes).digest("hex");
}

/**
 * sha256 (lowercase hex) of the file's raw bytes. `undefined` when the file
 * does not exist — a greenfield mission before survey, or an e2e fixture that
 * never shipped a blueprint. Any OTHER failure (EACCES, EISDIR, …) is thrown:
 * "could not read" is not "absent", and the dispatch guard must fail closed
 * on it rather than treat it as "nothing to check".
 */
export async function computeBlueprintHash(blueprintPath: string): Promise<string | undefined> {
  try {
    return (await computeFileSha256(blueprintPath)).toLowerCase();
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === "ENOENT") return undefined;
    throw err;
  }
}

/**
 * Returns a human-readable problem when the blueprint declares no real
 * non-goal, `undefined` when at least one non-placeholder bullet sits under
 * `## Ngoài phạm vi`. The section runs from that heading to the next heading
 * or `---`; bullets from later sections are never borrowed.
 */
export function assertNonGoalsPresent(blueprintText: string): string | undefined {
  const lines = blueprintText.split(/\r?\n/);
  // R-20: compare NFC-normalised on both sides — a heading typed on macOS in
  // NFD (combining diacritics) is visibly identical but byte-different. The
  // G0 hash itself (computeBlueprintHash) still hashes raw bytes untouched;
  // normalisation applies ONLY to this heading comparison.
  const normalizedHeading = NON_GOALS_HEADING.normalize("NFC");
  const start = lines.findIndex((line) => line.trim().normalize("NFC") === normalizedHeading);
  if (start === -1) {
    return (
      `BLUEPRINT thiếu mục "${NON_GOALS_HEADING}" — spec §13: không có non-goal thì ` +
      "không được duyệt G0. Thêm mục này với ít nhất một dòng gạch đầu dòng thật."
    );
  }

  let declared = 0;
  for (const line of lines.slice(start + 1)) {
    const trimmed = line.trim();
    if (SECTION_END.test(trimmed)) break;
    const bullet = BULLET.exec(trimmed);
    if (bullet === null) continue;
    const text = (bullet[1] ?? "").trim();
    if (text === "" || PLACEHOLDER_BULLET.test(text)) continue;
    declared += 1;
  }

  if (declared === 0) {
    return (
      `Mục "${NON_GOALS_HEADING}" của BLUEPRINT rỗng hoặc chỉ còn placeholder của template — ` +
      "ghi ít nhất một non-goal thật rồi mới request G0 (spec §13)."
    );
  }
  return undefined;
}
